import React from 'react';

const ClientCharts = ({ client }) => {
  // Validación defensiva
  if (!client || !client.meetings || !Array.isArray(client.meetings)) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#0061A8]">
        <h4 className="font-medium text-[#0061A8] mb-4">Rendimiento Mensual</h4>
        <p className="text-[#63666A] text-sm">No hay datos de juntas disponibles para mostrar gráficas.</p>
      </div>
    );
  }

  const meetings = client.meetings;

  // Agrupar datos por mes
  const monthlyData = meetings.reduce((acc, meeting) => {
    const date = new Date(meeting.date);
    const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`; // YYYY-M

    if (!acc[monthYear]) {
      acc[monthYear] = {
        contactsGenerated: 0,
        patients: 0,
        meetingsCount: 0,
        monthLabel: date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })
      };
    }

    acc[monthYear].contactsGenerated += meeting.contactsGenerated;
    acc[monthYear].patients += meeting.patients;
    acc[monthYear].meetingsCount += 1;

    return acc;
  }, {});

  // Convertir a array y ordenar por fecha
  const sortedMonthlyData = Object.keys(monthlyData)
    .sort((a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    })
    .map(key => {
      const data = monthlyData[key];
      const conversionRate = data.contactsGenerated > 0 ? (data.patients / data.contactsGenerated) * 100 : 0;
      return {
        month: data.monthLabel,
        contacts: data.contactsGenerated,
        patients: data.patients,
        conversion: parseFloat(conversionRate.toFixed(1))
      };
    });

  // Función para dibujar una gráfica de barras verticales
  const renderBarChart = (data) => {
    if (data.length < 1) {
      return (
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#0061A8]">
          <p className="text-[#63666A] text-sm">No hay datos suficientes para mostrar la gráfica de rendimiento.</p>
        </div>
      );
    }

    const chartHeight = 350;
    const chartWidth = Math.max(800, data.length * 120); // Ancho dinámico basado en cantidad de datos
    const padding = { top: 40, right: 40, bottom: 80, left: 60 };
    const graphHeight = chartHeight - padding.top - padding.bottom;
    const graphWidth = chartWidth - padding.left - padding.right;

    // Encontrar el valor máximo para escalar
    const maxContacts = Math.max(...data.map(d => d.contacts));
    const maxPatients = Math.max(...data.map(d => d.patients));
    const maxValue = Math.max(maxContacts, maxPatients, 50); // Mínimo 50 para mejor visualización

    // Configuración de barras
    const barGroupWidth = graphWidth / data.length;
    const barWidth = Math.min(barGroupWidth / 4, 25); // 3 barras + espacio
    const barSpacing = barWidth * 0.2;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#0061A8] overflow-x-auto">
        <div className="flex justify-between items-center mb-6">
          <h4 className="font-medium text-[#0061A8]">Rendimiento Mensual (Barras Verticales)</h4>
          <div className="flex space-x-4 text-sm">
            <div className="flex items-center">
              <span className="w-3 h-3 bg-[#0061A8] mr-1"></span>
              <span className="text-[#63666A]">Contactos</span>
            </div>
            <div className="flex items-center">
              <span className="w-3 h-3 bg-[#A8D08D] mr-1"></span>
              <span className="text-[#63666A]">Pacientes</span>
            </div>
            <div className="flex items-center">
              <span className="w-3 h-3 bg-[#63666A] mr-1"></span>
              <span className="text-[#63666A]">Conversión (%)</span>
            </div>
          </div>
        </div>

        <svg width={chartWidth} height={chartHeight} className="mx-auto">
          {/* Líneas de referencia horizontales */}
          {[0, 25, 50, 75, 100].map((value, i) => {
            const y = chartHeight - padding.bottom - ((value / Math.max(maxValue, 100)) * graphHeight);
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={chartWidth - padding.right}
                  y2={y}
                  stroke="#f0f0f0"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 10}
                  y={y}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="text-xs fill-[#63666A]"
                >
                  {value}
                </text>
              </g>
            );
          })}

          {/* Barras y etiquetas */}
          {data.map((d, i) => {
            const groupX = padding.left + (i * barGroupWidth) + (barGroupWidth / 2);
            
            // Posiciones de las tres barras
            const contactsX = groupX - barWidth - barSpacing;
            const patientsX = groupX;
            const conversionX = groupX + barWidth + barSpacing;

            // Alturas de las barras
            const contactsHeight = (d.contacts / maxValue) * graphHeight;
            const patientsHeight = (d.patients / maxValue) * graphHeight;
            const conversionHeight = (d.conversion / 100) * graphHeight;

            return (
              <g key={i}>
                {/* Barra de Contactos */}
                <rect
                  x={contactsX - barWidth/2}
                  y={chartHeight - padding.bottom - contactsHeight}
                  width={barWidth}
                  height={contactsHeight}
                  fill="#0061A8"
                  rx="2"
                />
                <text
                  x={contactsX}
                  y={chartHeight - padding.bottom - contactsHeight - 5}
                  textAnchor="middle"
                  className="text-xs fill-[#0061A8] font-medium"
                >
                  {d.contacts}
                </text>

                {/* Barra de Pacientes */}
                <rect
                  x={patientsX - barWidth/2}
                  y={chartHeight - padding.bottom - patientsHeight}
                  width={barWidth}
                  height={patientsHeight}
                  fill="#A8D08D"
                  rx="2"
                />
                <text
                  x={patientsX}
                  y={chartHeight - padding.bottom - patientsHeight - 5}
                  textAnchor="middle"
                  className="text-xs fill-[#A8D08D] font-medium"
                >
                  {d.patients}
                </text>

                {/* Barra de Conversión */}
                <rect
                  x={conversionX - barWidth/2}
                  y={chartHeight - padding.bottom - conversionHeight}
                  width={barWidth}
                  height={conversionHeight}
                  fill="#63666A"
                  rx="2"
                />
                <text
                  x={conversionX}
                  y={chartHeight - padding.bottom - conversionHeight - 5}
                  textAnchor="middle"
                  className="text-xs fill-[#63666A] font-medium"
                >
                  {d.conversion}%
                </text>

                {/* Etiqueta del mes */}
                <text
                  x={groupX}
                  y={chartHeight - padding.bottom + 20}
                  textAnchor="middle"
                  className="text-sm fill-[#63666A] font-medium"
                >
                  {d.month}
                </text>

                {/* Línea separadora entre grupos */}
                {i < data.length - 1 && (
                  <line
                    x1={groupX + barGroupWidth/2}
                    y1={padding.top}
                    x2={groupX + barGroupWidth/2}
                    y2={chartHeight - padding.bottom}
                    stroke="#f8f9fa"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  const generateInsights = (data, allMeetings) => {
    let insights = [];

    // Interpretación de la gráfica de barras verticales
    insights.push(`\n*Interpretación de la Gráfica de Rendimiento Mensual (Barras Verticales):*`);
    insights.push(`  - Cada grupo de tres barras representa un mes. La primera barra (azul) muestra los Contactos generados, la segunda (verde) los Pacientes concretados, y la tercera (gris) la Tasa de Conversión (en porcentaje).`);
    insights.push(`  - La altura de cada barra indica la magnitud de la métrica para ese mes. Puedes comparar visualmente el desempeño de cada métrica a lo largo del tiempo.`);
    insights.push(`  - Observa cómo se relacionan las barras: un aumento en Contactos sin un aumento proporcional en Pacientes podría indicar una baja eficiencia en la conversión, mientras que un aumento en Pacientes con Contactos estables podría significar una mejora en la calidad de los contactos o en el proceso de seguimiento.`);
    insights.push(`  - La barra gris de Conversión te da una idea directa de la efectividad de tus esfuerzos para transformar contactos en pacientes en cada periodo.`);

    // Comparación Junta Actual vs Junta Anterior
    if (allMeetings.length >= 2) {
      const lastMeeting = allMeetings[allMeetings.length - 1];
      const prevMeeting = allMeetings[allMeetings.length - 2];

      insights.push(`\n*Comparación Junta Actual (${new Date(lastMeeting.date).toLocaleDateString('es-MX')}) vs Junta Anterior (${new Date(prevMeeting.date).toLocaleDateString('es-MX')}):*`);
      
      // Contactos
      if (lastMeeting.contactsGenerated > prevMeeting.contactsGenerated) {
        insights.push(`  - Contactos generados: Aumento de ${prevMeeting.contactsGenerated} a ${lastMeeting.contactsGenerated} (+${lastMeeting.contactsGenerated - prevMeeting.contactsGenerated}).`);
      } else if (lastMeeting.contactsGenerated < prevMeeting.contactsGenerated) {
        insights.push(`  - Contactos generados: Disminución de ${prevMeeting.contactsGenerated} a ${lastMeeting.contactsGenerated} (${lastMeeting.contactsGenerated - prevMeeting.contactsGenerated}).`);
      } else {
        insights.push(`  - Contactos generados: Se mantuvieron estables en ${lastMeeting.contactsGenerated}.`);
      }

      // Pacientes
      if (lastMeeting.patients > prevMeeting.patients) {
        insights.push(`  - Pacientes concretados: Aumento de ${prevMeeting.patients} a ${lastMeeting.patients} (+${lastMeeting.patients - prevMeeting.patients}).`);
      } else if (lastMeeting.patients < prevMeeting.patients) {
        insights.push(`  - Pacientes concretados: Disminución de ${prevMeeting.patients} a ${lastMeeting.patients} (${lastMeeting.patients - prevMeeting.patients}).`);
      } else {
        insights.push(`  - Pacientes concretados: Se mantuvieron estables en ${lastMeeting.patients}.`);
      }

      // Tasa de Conversión
      const lastConversion = lastMeeting.contactsGenerated > 0 ? (lastMeeting.patients / lastMeeting.contactsGenerated) * 100 : 0;
      const prevConversion = prevMeeting.contactsGenerated > 0 ? (prevMeeting.patients / prevMeeting.contactsGenerated) * 100 : 0;
      
      if (lastConversion > prevConversion) {
        insights.push(`  - Tasa de Conversión: Mejoró del ${prevConversion.toFixed(1)}% al ${lastConversion.toFixed(1)}%.`);
      } else if (lastConversion < prevConversion) {
        insights.push(`  - Tasa de Conversión: Disminuyó del ${prevConversion.toFixed(1)}% al ${lastConversion.toFixed(1)}%.`);
      } else {
        insights.push(`  - Tasa de Conversión: Se mantuvo en ${lastConversion.toFixed(1)}%.`);
      }
    } else {
      insights.push("No hay suficientes juntas para realizar una comparación entre la junta actual y la anterior.");
    }

    // Interpretación de la tasa de conversión mensual
    if (data.length > 0) {
      const avgConversion = data.reduce((sum, d) => sum + d.conversion, 0) / data.length;
      insights.push(`\n*Análisis Mensual de Tasa de Conversión:*`);
      insights.push(`  - La tasa de conversión promedio mensual es del ${avgConversion.toFixed(1)}%.`);
      if (data.length >= 2) {
        const lastMonthData = data[data.length - 1];
        const prevMonthData = data[data.length - 2];
        if (lastMonthData.conversion > prevMonthData.conversion) {
          insights.push(`  - Se observa una mejora en la conversión del último mes (${lastMonthData.conversion}%) respecto al anterior (${prevMonthData.conversion}%).`);
        } else if (lastMonthData.conversion < prevMonthData.conversion) {
          insights.push(`  - La conversión del último mes (${lastMonthData.conversion}%) ha disminuido respecto al anterior (${prevMonthData.conversion}%).`);
        }
      }
    }

    // Recomendaciones
    insights.push(`\n*Recomendaciones y Posibles Acciones:*`);
    if (data.length > 0) {
      const lastMonthData = data[data.length - 1];
      if (lastMonthData.conversion < 50) {
        insights.push(`  - Evaluar el proceso de calificación de contactos o el seguimiento para mejorar la conversión de ${lastMonthData.conversion}%.`);
      } else if (lastMonthData.conversion >= 70) {
        insights.push(`  - Mantener las estrategias actuales de conversión y buscar escalar la generación de contactos.`);
      }
      if (lastMonthData.contacts === 0) {
        insights.push(`  - Urgente: Implementar acciones para generar nuevos contactos este mes.`);
      }
    } else {
      insights.push(`  - Registrar más juntas para obtener datos y generar recomendaciones más precisas.`);
    }

    return insights.join('\n');
  };

  const insightsReport = generateInsights(sortedMonthlyData, meetings);

  return (
    <div className="space-y-6">
      {renderBarChart(sortedMonthlyData)}

      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#63666A]">
        <h4 className="font-medium text-[#0061A8] mb-4">Reporte de Insights Clave</h4>
        <pre className="whitespace-pre-wrap font-sans text-sm text-[#63666A]">{insightsReport}</pre>
      </div>
    </div>
  );
};

export default ClientCharts;
